import { useMemo, useState } from "react";
import { Link, router } from "@inertiajs/react";
import { Search, Target } from "lucide-react";
import { ListTemplate } from "@/templates/ListTemplate";
import { Table } from "@/components/Table";
import { EmptyState } from "@/components/EmptyState";
import { Input } from "@/components/Input";

interface GoalRow {
  id: string;
  objId: string;
  name: string;
  projectId: string | null;
  projectName: string | null;
  targetMinor: number;
  allocatedMinor: number;
  contributionsCount: number;
  contributorCount: number;
  progressPct: number | null;
  lastContributedAt: string | null;
}

interface Props {
  goals: GoalRow[];
}

function fmtMoneyCompact(minor: number, currency = "£") {
  const major = minor / 100;
  if (Math.abs(major) >= 1000)
    return `${currency}${(major / 1000).toLocaleString(undefined, { maximumFractionDigits: 1 })}K`;
  return `${currency}${major.toLocaleString(undefined, { maximumFractionDigits: 0 })}`;
}

function fmtRelative(iso: string | null) {
  if (!iso) return "—";
  const d = Math.floor((Date.now() - new Date(iso).getTime()) / 86400000);
  if (d <= 0) return "today";
  if (d === 1) return "yesterday";
  if (d < 30) return `${d}d ago`;
  if (d < 365) return `${Math.floor(d / 30)}mo ago`;
  return `${Math.floor(d / 365)}y ago`;
}

export default function GoalsIndex({ goals }: Props) {
  const [q, setQ] = useState("");

  const filtered = useMemo(() => {
    const s = q.trim().toLowerCase();
    if (!s) return goals;
    return goals.filter(
      (g) =>
        g.name.toLowerCase().includes(s) ||
        (g.projectName ?? "").toLowerCase().includes(s) ||
        g.objId.toLowerCase().includes(s),
    );
  }, [goals, q]);

  return (
    <ListTemplate
      title="Goals"
      description="The specific impact goals your received contributions have funded."
      breadcrumbs={[{ label: "Home", to: "/" }, { label: "Goals" }]}
      meta={
        <span className="hidden sm:inline-flex items-center text-sm text-fg-muted tabular">
          <span className="font-medium text-fg">{goals.length}</span>
          <span className="ml-1">goals</span>
        </span>
      }
      filters={
        <div className="w-full sm:w-72">
          <Input
            leadingIcon={<Search />}
            placeholder="Search goals, projects…"
            value={q}
            onChange={(e) => setQ(e.target.value)}
          />
        </div>
      }
    >
      {filtered.length === 0 ? (
        <EmptyState
          icon={<Target />}
          title={q ? "No goals match" : "No goals yet"}
          description={q ? "Try a different search." : "Goals in your projects will appear here."}
        />
      ) : (
        <Table>
          <Table.Header>
            <tr>
              <Table.Head className="pl-4">Goal</Table.Head>
              <Table.Head>Project</Table.Head>
              <Table.Head className="text-right">Allocated</Table.Head>
              <Table.Head className="text-right">Contributions</Table.Head>
              <Table.Head className="w-40">Progress</Table.Head>
              <Table.Head>Last activity</Table.Head>
            </tr>
          </Table.Header>
          <Table.Body>
            {filtered.map((g) => {
              const url = `/goals/${g.id}`;
              return (
                <Table.Row
                  key={g.id}
                  className="cursor-pointer"
                  onClick={() => router.visit(url)}
                  tabIndex={0}
                  onKeyDown={(e) => {
                    if (e.key === "Enter") router.visit(url);
                  }}
                >
                  <Table.Cell className="pl-4">
                    <div className="flex items-center gap-2.5" onClick={(e) => e.stopPropagation()}>
                      <span className="flex size-7 shrink-0 items-center justify-center rounded-[var(--radius-md)] bg-accent-subtle text-accent-text [&_svg]:size-4">
                        <Target />
                      </span>
                      <div className="min-w-0">
                        <Link
                          href={url}
                          className="block truncate font-medium text-fg hover:text-accent-text transition-colors"
                        >
                          {g.name}
                        </Link>
                        <div className="truncate font-mono text-xs text-fg-muted">{g.objId}</div>
                      </div>
                    </div>
                  </Table.Cell>
                  <Table.Cell className="text-fg-muted truncate" onClick={(e) => e.stopPropagation()}>
                    {g.projectId ? (
                      <Link
                        href={`/projects/${g.projectId}`}
                        className="hover:text-accent-text transition-colors"
                      >
                        {g.projectName ?? "—"}
                      </Link>
                    ) : (
                      g.projectName ?? <span className="text-fg-subtle">—</span>
                    )}
                  </Table.Cell>
                  <Table.Cell className="text-right tabular text-fg">
                    {fmtMoneyCompact(g.allocatedMinor)}
                  </Table.Cell>
                  <Table.Cell className="text-right tabular text-fg-muted">
                    {g.contributionsCount.toLocaleString()}
                  </Table.Cell>
                  <Table.Cell>
                    {g.progressPct == null ? (
                      <span className="text-xs text-fg-subtle">Open-ended</span>
                    ) : (
                      <div className="flex items-center gap-2">
                        <div className="h-1.5 flex-1 overflow-hidden rounded-full bg-muted">
                          <div
                            className="h-full rounded-full bg-accent"
                            style={{ width: `${g.progressPct}%` }}
                          />
                        </div>
                        <span className="shrink-0 tabular text-xs text-fg-muted">{g.progressPct}%</span>
                      </div>
                    )}
                  </Table.Cell>
                  <Table.Cell className="text-fg-muted tabular whitespace-nowrap">
                    {fmtRelative(g.lastContributedAt)}
                  </Table.Cell>
                </Table.Row>
              );
            })}
          </Table.Body>
        </Table>
      )}
    </ListTemplate>
  );
}
